import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import communityApi from 'src/api/communityApi';
import { userEntity, usersEntity } from "./usersActions";
import { CommentInterface } from "src/store/community/commentsSlice";
import { UserInterface } from "src/store/community/usersSlice";
import communityUrls from "src/configs/community/communityUrls";

const PREFIX = 'community';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: communityUrls.commentPath(value),
    comments: []
  };
}

export const commentsEntity = new schema.Entity(
  'comments',
  {
    sender: usersEntity
  },
  {
    processStrategy
  }
);

export const commentEntity = new schema.Entity(
  'comment',
  {
    sender: userEntity
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any, {
    user: { [key: string]: UserInterface };
    comment: CommentInterface;
  }
  >(response.data.comment, commentEntity);
  return normalized.entities;
}

const commentRequest = async (params: object = {}) => {
  const response    = await communityApi.get(`/onesky/comments`, { params })
  const normalized  = normalize<
    any, {
    users: { [key: string]: UserInterface };
    comments: { [key: string]: CommentInterface }
  }
    >(response.data.comments, [commentsEntity]);

  return {
    users: normalized.entities.users || {},
    comments: normalized.entities.comments || {},
    ids: normalized.result,
    count: response.data.meta?.total_count || 0
  }
}

export const fetchComments = createAsyncThunk(
  `${PREFIX}_comments/fetchComments`,
  commentRequest
);

export const fetchSentComments = createAsyncThunk(
  `${PREFIX}_comments/fetchComments`,
  async ({ userId, params }: { userId: number, params: object }) => {
    const response    = await communityApi.get(`/onesky/users/${userId}/sent_comments`, { params })
    const normalized  = normalize<
      any, {
      users: { [key: string]: UserInterface };
      comments: { [key: string]: CommentInterface }
    }
      >(response.data.comments, [commentsEntity]);

    return {
      users: normalized.entities.users || {},
      comments: normalized.entities.comments || {},
      ids: normalized.result,
      count: response.data.meta?.total_count || 0
    }
  }
);

export const fetchReplies = createAsyncThunk(
  `${PREFIX}_comments/fetchReplies`,
  commentRequest
);

export const fetchComment = createAsyncThunk(
  `${PREFIX}_comments/fetchComment`,
  async (id: number) => {
    const response = await communityApi.get(`/onesky/comments/${id}`);
    return normalizeInstance(response);
  }
);

export const updateComment = createAsyncThunk(
  `${PREFIX}_comments/updateComment`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/comments/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyComment = createAsyncThunk(
  `${PREFIX}_comments/destroyComment`,
  async (id: number) => {
    await communityApi.delete(`/onesky/comments/${id}`);
    return { id: id };
  }
);
